import * as React from "react"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { Save, ShieldCheck } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MemberShell } from "@/components/member-shell"
import { getAccountFn, updateProfileFn } from "@/lib/app.functions"
import { requireMemberRoute } from "@/lib/route-guards"

export const Route = createFileRoute("/account")({
  loader: async () => {
    await requireMemberRoute("/account")
    return getAccountFn()
  },
  head: () => ({
    meta: [{ title: "Account settings · Harbor & Pine Realty" }],
  }),
  component: AccountPage,
})

function AccountPage() {
  const { user } = Route.useLoaderData()
  const updateProfile = useServerFn(updateProfileFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    const newPassword = String(form.get("newPassword") ?? "")
    try {
      await updateProfile({
        data: {
          name: String(form.get("name") ?? ""),
          phone: String(form.get("phone") ?? ""),
          locale: String(form.get("locale")) as "en" | "fr" | "zh",
          currentPassword:
            String(form.get("currentPassword") ?? "") || undefined,
          newPassword: newPassword || undefined,
        },
      })
      await router.invalidate()
      toast.success("Account details saved.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save.")
    } finally {
      setPending(false)
    }
  }

  return (
    <MemberShell
      user={user}
      eyebrow="Identity · profile preferences"
      title="Account"
      intro="Profile fields and language preference persist in D1. Public demo account passwords are intentionally fixed."
    >
      <form onSubmit={handleSubmit} className="max-w-3xl space-y-7">
        <fieldset className="grid gap-5 border bg-white p-5 sm:grid-cols-2">
          <legend className="field-label bg-background px-2">Profile</legend>
          <AccountField label="Name" htmlFor="name">
            <Input
              id="name"
              name="name"
              required
              defaultValue={user.name}
              className="field-input"
            />
          </AccountField>
          <AccountField label="Email" htmlFor="email">
            <Input
              id="email"
              name="email"
              value={user.email}
              disabled
              className="field-input"
            />
          </AccountField>
          <AccountField label="Phone" htmlFor="phone">
            <Input
              id="phone"
              name="phone"
              required
              defaultValue={user.phone}
              className="field-input"
            />
          </AccountField>
          <AccountField label="Preferred language" htmlFor="locale">
            <select
              id="locale"
              name="locale"
              defaultValue={user.locale}
              className="field-input"
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
              <option value="zh">中文</option>
            </select>
          </AccountField>
        </fieldset>

        <fieldset className="grid gap-5 border bg-white p-5 sm:grid-cols-2">
          <legend className="field-label bg-background px-2">
            Optional password change
          </legend>
          <div className="flex gap-3 border border-primary bg-secondary p-4 text-sm leading-6 sm:col-span-2">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" />
            <p>
              Shared member and administrator passwords cannot be changed.
              Create a disposable profile to exercise this workflow safely.
            </p>
          </div>
          <AccountField label="Current password" htmlFor="currentPassword">
            <Input
              id="currentPassword"
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              className="field-input"
            />
          </AccountField>
          <AccountField label="New password" htmlFor="newPassword">
            <Input
              id="newPassword"
              name="newPassword"
              type="password"
              minLength={10}
              autoComplete="new-password"
              className="field-input"
            />
          </AccountField>
        </fieldset>

        <Button
          type="submit"
          className="field-button"
          size="lg"
          disabled={pending}
        >
          <Save />
          {pending ? "Saving…" : "Save account"}
        </Button>
      </form>
    </MemberShell>
  )
}

function AccountField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="field-label">
        {label}
      </Label>
      {children}
    </div>
  )
}
